import {
  AbsoluteTimeRange,
  FlexibleTimeRange,
  FlexibleTimeValue,
  RelativeTimeRange,
  useFeatureFlags,
} from '@chronosphereio/core';
import { DateCalendar, DateRangeCalendar, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { Button } from '../../Button';
import { SimpleSelect } from '../../SimpleSelect';
import { Stack } from '../../Stack';
import { TextField } from '../../TextField';
import { ToggleButton, ToggleButtonGroup } from '../../ToggleButtonGroup';
import { BaseCalendarProps, BaseInputCalendarWrapper } from './BaseInputCalendarWrapper';
import { AROUND_A_TIME_DURATION_OPTIONS, useFlexibleCalendarState } from './useFlexibleCalendarState';

export type { FlexibleTimeValue, FlexibleTimeRange };

/**
 * A time range value that can be either absolute or relative.
 */
export type TimeRangeValue = AbsoluteTimeRange | RelativeTimeRange;

export type TimeRangeSelectionMode = 'fromTo' | 'aroundATime';

/** State for "Around a time" mode in the date/time picker. */
export interface AroundATimeState {
  /** The center time value (e.g. "now", "now-15m", or a formatted date string). */
  center: string;
  /** The selected ± duration (e.g. "15m", "1h"). */
  duration: string;
  /** Callback to update the center time value. */
  setCenter: (center: string) => void;
  /** Callback to update the ± duration. */
  setDuration: (duration: string) => void;
}

export interface FlexibleInputCalendarProps extends BaseCalendarProps {
  /** State for "Around a time" mode. */
  aroundATime: AroundATimeState;
  /**
   * Use flexible text input fields that accept relative time expressions
   * like "now", "now-5m", or bare durations like "5m".
   */
  inputMode: 'flexible';
  /**
   * Callback when the user applies a time range.
   * Returns a TimeRangeValue which can be either:
   * - AbsoluteTimeRange: { start: Date, end: Date }
   * - RelativeTimeRange: { pastDuration: string } (e.g., "5m", "1h")
   */
  onApply: (timeRange: TimeRangeValue) => void;
  /** Initial time range that can include relative time expressions. */
  initialTimeRange?: FlexibleTimeRange;
  /** Callback to update the current selection mode. */
  setTimeRangeSelectionMode?: (mode: TimeRangeSelectionMode) => void;
  /** Whether the picker is in "from/to" or "around a time" mode. */
  timeRangeSelectionMode?: TimeRangeSelectionMode;
}

export const FlexibleInputCalendar = ({
  initialTimeRange,
  minDate,
  maxDate,
  onApply,
  timeFormat,
  timeZone,
  aroundATime,
  timeRangeSelectionMode = 'fromTo',
  setTimeRangeSelectionMode = () => {},
}: FlexibleInputCalendarProps) => {
  const { enableAroundATime } = useFeatureFlags();
  const { duration: durationValue, setDuration: setDurationValue } = aroundATime;

  const {
    startInputValue,
    endInputValue,
    calendarValue,
    rangePosition,
    isValidTime,
    startHasError,
    endHasError,
    hasRangeOrderError,
    startFormatHint,
    endHelperText,
    onClickApply,
    setEndTimeToNow,
    handleTimeRangeSelectionModeChange,
    handleStartInputChange,
    handleEndInputChange,
    handleStartKeyDown,
    normalizeStartInput,
    handleMonthChange,
    handleCalendarWrapperClick,
    handleRangePositionChange,
    handleDateRangeChange,
    handleSingleDateChange,
  } = useFlexibleCalendarState({
    initialTimeRange,
    timeFormat,
    timeZone,
    aroundATime,
    timeRangeSelectionMode,
    setTimeRangeSelectionMode,
    onApply,
    minDate,
    maxDate,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack
        direction="column"
        sx={(theme) => ({
          background: 'inherit',
          padding: theme.spacing(2),
        })}
      >
        <Stack alignItems="center" sx={{ width: '100%' }}>
          <Stack sx={{ width: '100%' }} gap={(theme) => theme.spacing(2)}>
            {enableAroundATime && (
              <ToggleButtonGroup
                value={timeRangeSelectionMode}
                onChange={handleTimeRangeSelectionModeChange}
                fullWidth
                sx={{ width: '100%' }}
              >
                <ToggleButton value="fromTo" sx={{ flex: 1 }}>
                  From / To
                </ToggleButton>
                <ToggleButton value="aroundATime" sx={{ flex: 1 }}>
                  Around a time
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {timeRangeSelectionMode === 'fromTo' ? (
              <>
                <TextField
                  label="From"
                  value={startInputValue}
                  onChange={(e) => handleStartInputChange(e.target.value)}
                  onBlur={normalizeStartInput}
                  onKeyDown={handleStartKeyDown}
                  error={startHasError}
                  helperText={startHasError ? startFormatHint : undefined}
                  inputProps={{
                    'aria-label': 'From date time',
                  }}
                />
                <TextField
                  label="To"
                  value={endInputValue}
                  onChange={(e) => handleEndInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onClickApply();
                    }
                  }}
                  error={endHasError || hasRangeOrderError}
                  helperText={endHelperText}
                  inputProps={{
                    'aria-label': 'To date time',
                  }}
                />
                <Stack alignItems="flex-end" sx={{ margin: (theme) => theme.spacing(-1, 0, 1, 0) }}>
                  <Button size="small" variant="tertiary" onClick={setEndTimeToNow}>
                    Set to now
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <TextField
                  label="Time"
                  value={startInputValue}
                  onChange={(e) => handleStartInputChange(e.target.value)}
                  onKeyDown={handleStartKeyDown}
                  inputProps={{
                    'aria-label': 'Around a time center',
                  }}
                />
                <SimpleSelect
                  label="Duration"
                  value={durationValue}
                  onChange={(e) => setDurationValue(e.target.value)}
                  options={AROUND_A_TIME_DURATION_OPTIONS}
                  fullWidth
                />
              </>
            )}
          </Stack>
        </Stack>
        {timeRangeSelectionMode === 'aroundATime' ? (
          <BaseInputCalendarWrapper>
            <DateCalendar
              data-testid="date-calendar"
              value={calendarValue.start}
              onChange={handleSingleDateChange}
              minDate={minDate}
              maxDate={maxDate}
            />
          </BaseInputCalendarWrapper>
        ) : (
          <BaseInputCalendarWrapper onClickCapture={handleCalendarWrapperClick}>
            <DateRangeCalendar<Date | null>
              data-testid="date-range-calendar"
              value={[calendarValue.start, calendarValue.end]}
              onChange={handleDateRangeChange}
              rangePosition={rangePosition}
              onMonthChange={handleMonthChange}
              onRangePositionChange={handleRangePositionChange}
              minDate={minDate}
              maxDate={maxDate}
            />
          </BaseInputCalendarWrapper>
        )}
        <Stack direction="row" sx={(theme) => ({ gap: theme.spacing(1), justifyContent: 'flex-end' })}>
          <Button variant="primary" onClick={onClickApply} disabled={!isValidTime}>
            Apply time range
          </Button>
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};
